import React, { useCallback, useEffect, useState } from "react";
import { ChefHat, Martini, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";
import { apiErrorMessage } from "@/lib/apiError";

const ageLabel = (minutes) => {
  const m = Number(minutes) || 0;
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) return `${Math.round(m / 60)} h`;
  return `${Math.round(m / 1440)} days`;
};

/**
 * Session 74 — Owner banner: tickets piling up on a Kitchen / Bar screen
 * nobody is working (stale > 12 h). Explains the two fixes (route the
 * department elsewhere, or clear the stale tickets — status only, no money)
 * and offers the clear with a confirmation.
 */
export const StationHealthBanner = ({ http, authHeaders, onOpenDepartments }) => {
  const [health, setHealth] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [confirm, setConfirm] = useState(null); // { station, count, oldest }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await http.get("/owner/stations/health", { headers: authHeaders });
      setHealth(r.data || null);
    } catch (_) { /* banner is best-effort */ }
  }, [http, authHeaders]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const clearStale = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const r = await http.post(`/owner/stations/${confirm.station}/clear-stale`, { hours: health?.[confirm.station]?.stale_after_hours || 12 }, { headers: authHeaders, timeout: 120000 });
      toast.success(`${r.data?.tickets || 0} stale ${confirm.station} ticket${r.data?.tickets === 1 ? "" : "s"} marked as served. Sales and reports are unchanged.`);
      setConfirm(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not clear the stale tickets."));
    } finally {
      setBusy(false);
    }
  };

  if (!health || hidden) return null;
  const stations = ["kitchen", "bar"].map((s) => ({ key: s, ...(health[s] || {}) })).filter((s) => (s.stale || 0) > 0);
  if (!stations.length) return null;

  return (
    <div className="relative rounded-[24px] border border-sky-300 bg-sky-50/95 p-4 pr-14 shadow-md backdrop-blur-md dark:border-sky-900/60 dark:bg-sky-950/70" data-testid="station-health-banner">
      <p className="text-sm font-semibold text-sky-900 dark:text-sky-100">Tickets are piling up on a screen nobody is working</p>
      <p className="mt-0.5 text-xs text-sky-800 dark:text-sky-200">
        These were never marked ready on their station. They are already counted in sales — this only tidies the screens. If you don't use a screen, open <button type="button" className="font-medium underline underline-offset-2" onClick={onOpenDepartments} data-testid="station-health-open-departments">Departments</button> and untick "Send to {stations.map((s) => s.key).join(" / ")}" for those items so new orders stop queueing there.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {stations.map((s) => (
          <div key={s.key} className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky-200 bg-white/70 px-3 py-2 text-xs dark:border-sky-900/50 dark:bg-sky-950/50" data-testid={`station-health-${s.key}`}>
            {s.key === "kitchen" ? <ChefHat className="h-4 w-4 text-sky-700 dark:text-sky-300" /> : <Martini className="h-4 w-4 text-sky-700 dark:text-sky-300" />}
            <span className="font-semibold capitalize text-sky-900 dark:text-sky-100">{s.key}</span>
            <span className="text-sky-800 dark:text-sky-200" data-testid={`station-health-${s.key}-stale`}>{s.stale} ticket{s.stale === 1 ? "" : "s"} older than {s.stale_after_hours} h · oldest {ageLabel(s.oldest_minutes)} · {s.waiting} waiting in total</span>
            <Button size="sm" variant="outline" className="h-7 rounded-full border-sky-300 bg-white text-sky-900 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100" onClick={() => setConfirm({ station: s.key, count: s.stale, oldest: s.oldest_minutes })} data-testid={`station-health-${s.key}-clear`}>
              Mark {s.stale} as served
            </Button>
          </div>
        ))}
      </div>
      {health.open_paid_tables_over_24h > 0 ? (
        <p className="mt-2 text-[11px] text-sky-800/90 dark:text-sky-200/90" data-testid="station-health-open-paid">
          {health.open_paid_tables_over_24h} paid table{health.open_paid_tables_over_24h === 1 ? " has" : "s have"} been left open for more than a day — closing a table after payment is what clears its tickets everywhere.
        </p>
      ) : null}
      <button type="button" onClick={() => setHidden(true)} className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-sky-900 transition-colors hover:bg-sky-200/70 dark:text-sky-100 dark:hover:bg-sky-900/60" aria-label="Hide for now" data-testid="station-health-dismiss">
        <X className="h-4 w-4" />
      </button>

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o && !busy) setConfirm(null); }}>
        <AlertDialogContent data-testid="station-health-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {confirm?.count} stale {confirm?.station} ticket{confirm?.count === 1 ? "" : "s"} as served?</AlertDialogTitle>
            <AlertDialogDescription>
              Every {confirm?.station} line on tickets older than {health?.[confirm?.station]?.stale_after_hours || 12} hours (oldest {ageLabel(confirm?.oldest)}) will be marked ready and served, and they will leave the {confirm?.station} screen, runner and waiter lists. Nothing changes in sales, payments, stock or reports — those tickets were already counted when they were rung up. This is written to the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="station-health-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); clearStale(); }} disabled={busy} data-testid="station-health-confirm-ok">{busy ? "Working…" : "Mark as served"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StationHealthBanner;
